import { WalletBalance, WalletBillingSource, WalletPrices, WalletProductPrice } from '../../../models/Wallet';

/**
 * Estimativa VISUAL de custo de um SMS Massivo (passo "Revisar").
 *
 * Não é a fonte da verdade: quem decide quanto cobrar (e se há saldo) é o backend,
 * que responde 422 + modal de confirmação. Esta função só nunca pode induzir o
 * usuário a uma decisão errada — na dúvida, devolve `unknown` ("calculado no envio").
 *
 * Dinheiro sempre em micros (R$ × 1_000_000), inteiros.
 */
export type BroadcastCostKind = 'wallet' | 'credits' | 'unknown';

export interface BroadcastCostEstimate {
  kind: BroadcastCostKind;
  /** Só quando kind === 'wallet'. Em micros (R$ × 1_000_000). */
  totalMicros?: number;
  availableMicros?: number;
  /** availableMicros - totalMicros; pode ser negativo. */
  remainingMicros?: number;
  insufficientFunds?: boolean;
}

const UNKNOWN: BroadcastCostEstimate = { kind: 'unknown' };

/** Inteiro finito (sem Number.isInteger/isFinite para não depender do lib target). */
function isFiniteInteger(n: unknown): n is number {
  return typeof n === 'number' && isFinite(n) && Math.floor(n) === n;
}

/**
 * Suposição: deveria existir no máximo um produto 'sms' em `prices.products`.
 * Se vier mais de um (não deveria), usamos o PRIMEIRO encontrado — é a mesma
 * ordem que o backend devolveu, e não temos critério melhor para desempatar.
 * O campo `unit` não é validado aqui (quem popula `prices` já filtra).
 */
function findSmsProduct(prices: WalletPrices): WalletProductPrice | undefined {
  const products = Array.isArray(prices.products) ? prices.products : [];
  for (const p of products) {
    if (p && p.service === 'sms') {
      return p;
    }
  }
  return undefined;
}

export function estimateSmsCost(
  quantity: number,
  billing: WalletBillingSource | null | undefined,
  prices: WalletPrices | null | undefined,
  balance: WalletBalance | null | undefined,
): BroadcastCostEstimate {
  // Cliente de pacote: custo é em créditos, nunca em R$.
  if (billing === 'credits') {
    return { kind: 'credits' };
  }
  // Billing não resolvido (null/undefined/valor inesperado) → não dá para afirmar nada.
  if (billing !== 'wallet') {
    return UNKNOWN;
  }
  if (!prices || !balance) {
    return UNKNOWN;
  }
  // Quantidade deve ser inteiro positivo: 0 viraria "R$ 0,00" (parece grátis),
  // e fracionário/negativo indica dado inválido — não arredondamos silenciosamente.
  if (!isFiniteInteger(quantity) || quantity <= 0) {
    return UNKNOWN;
  }

  const sms = findSmsProduct(prices);
  if (!sms || sms.blocked === true) {
    return UNKNOWN;
  }
  const unitPrice = sms.unit_price_micros;
  // null = produto bloqueado/sem preço (NÃO é zero). Preço negativo ou não finito
  // é dado corrompido: preferimos "calculado no envio" a mostrar um valor errado.
  if (typeof unitPrice !== 'number' || !isFinite(unitPrice) || unitPrice < 0) {
    return UNKNOWN;
  }

  const availableMicros = balance.available_micros;
  if (typeof availableMicros !== 'number' || !isFinite(availableMicros)) {
    return UNKNOWN;
  }

  // Ambos deveriam ser inteiros; Math.round protege caso um venha fracionário por engano.
  const totalMicros = Math.round(quantity * unitPrice);
  // Acima de 2^53 a aritmética deixa de ser exata — não arriscamos uma comparação errada.
  if (totalMicros > 9007199254740991) {
    return UNKNOWN;
  }
  const remainingMicros = availableMicros - totalMicros;

  /*
   * `balance.overdraft` — SUPOSIÇÃO A CONFIRMAR:
   * tratamos `overdraft` como flag informativa ("a conta pode ficar negativa"),
   * e assumimos que `available_micros` já é o valor gasto-disponível real
   * (saldo - retido), SEM incluir nenhum limite de cheque especial.
   * Por isso a fórmula não muda: se remainingMicros < 0, sinalizamos
   * insufficientFunds = true mesmo com overdraft = true. Motivo: é a opção
   * conservadora — no pior caso o usuário vê um alerta e o backend deixa
   * enviar; nunca o contrário (dizer "dentro do saldo" e o backend recusar,
   * ou deixar a conta negativa sem o usuário perceber).
   * Se o negócio quiser outro texto para contas com overdraft (ex.: "vai
   * deixar o saldo negativo" em vez de "saldo insuficiente"), a tela pode
   * ler `balance.overdraft` diretamente; esta função não esconde o sinal.
   */
  return {
    kind: 'wallet',
    totalMicros,
    availableMicros,
    remainingMicros,
    insufficientFunds: remainingMicros < 0,
  };
}
